/**
 * Billing status badge + credit usage bar for the sidebar.
 *
 * Shows: tier badge, monthly credit usage progress, manage/upgrade action.
 */

import { useEffect, useState } from "react";
import {
  type BillingStatus as BillingStatusData,
  getBillingStatus,
  createPortalSession,
} from "@/api/client";

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  trial: "Trial",
  starter: "Starter",
  pro: "Pro",
};

const TIER_COLORS: Record<string, string> = {
  free: "#6b7280",
  trial: "#f59e0b",
  starter: "#3b82f6",
  pro: "#8b5cf6",
};

export function BillingStatusWidget({
  refreshKey,
  onUpgrade,
}: {
  refreshKey?: number;
  onUpgrade?: () => void;
}) {
  const [status, setStatus] = useState<BillingStatusData | null>(null);

  useEffect(() => {
    getBillingStatus().then(setStatus);
  }, [refreshKey]);

  if (!status) return null;

  const creditsUsed = status.credits_used ?? 0;
  const creditsLimit = status.credits_limit ?? 0;
  const creditsRemaining = status.credits_remaining ?? 0;

  const pct = creditsLimit > 0
    ? Math.min(100, (creditsUsed / creditsLimit) * 100)
    : 0;
  const tierColor = TIER_COLORS[status.tier] || TIER_COLORS.free;
  const isPaid = status.tier === "starter" || status.tier === "pro";
  const isTrial = status.trial_active === true;
  const showUpgrade = !isPaid;

  const handleManage = async () => {
    if (isPaid) {
      const url = await createPortalSession();
      if (url) window.location.href = url;
    }
  };

  const formatCredits = (n: number) => {
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  return (
    <div style={{
      padding: "8px 12px",
      borderTop: "1px solid var(--border-primary, #2a2a2a)",
      fontSize: "12px",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            background: tierColor,
            color: "#fff",
            padding: "1px 8px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
          }}>
            {TIER_LABELS[status.tier] || status.tier}
          </span>
          {isTrial && status.trial_days_remaining != null && (
            <span style={{
              color: "#f59e0b",
              fontSize: 11,
              fontWeight: 500,
            }}>
              {status.trial_days_remaining}d left
            </span>
          )}
        </div>
        {isPaid ? (
          <button
            onClick={handleManage}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary, #888)",
              cursor: "pointer",
              fontSize: 11,
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Manage
          </button>
        ) : showUpgrade && onUpgrade ? (
          <button
            onClick={onUpgrade}
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              padding: 0,
            }}
          >
            Upgrade
          </button>
        ) : null}
      </div>
      <div style={{
        background: "var(--bg-tertiary, #1a1a1a)",
        borderRadius: 4,
        height: 6,
        overflow: "hidden",
        marginBottom: 4,
      }}>
        <div style={{
          width: `${pct}%`,
          height: "100%",
          background: pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : tierColor,
          borderRadius: 4,
          transition: "width 0.3s ease",
        }} />
      </div>
      <div style={{
        color: "var(--text-secondary, #888)",
        display: "flex",
        justifyContent: "space-between",
      }}>
        <span>{formatCredits(creditsUsed)} credits used</span>
        <span>{formatCredits(creditsRemaining)} left</span>
      </div>
    </div>
  );
}
